import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { LazyMountIframe, useLazyMountIframe } from '@/components/tiptap-editor/tiptap-extension/lazy-mount-tool-iframe';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/tooltip/tooltip';
import { ZyEditToolButton } from '@/components/tiptap-editor/tiptap-ui/zyEditTool-button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import type { AttributeDef } from '@/lib/parseZyXML';
import { parseZyXML } from '@/lib/parseZyXML';
import { ensureStreamlitXmlId, type StreamlitAppOptions } from '@/lib/streamlit-utils';
import authService from '@/services/zybook-auth-service';
import { type ReactNodeViewProps } from '@tiptap/react';
import { Info } from 'lucide-react';
import { connectToChild } from 'penpal';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { StreamlitAppNodeOptions } from './streamlit-app-extension';
import { StreamlitPreviewIframe } from './streamlit-preview-iframe';

interface StreamlitChildConnection {
    getDraftState?: () => Promise<{ code: string; caption: string }>;
    saveForModernAuthoring?: () => Promise<void>;
    destroy: () => Promise<void>;
}

const parseStreamlitXMLConfig = {
    rootTag: 'zyTool',
    xmlPropName: 'streamlitXMLToLoad',
    attributeToRemoveFromXml: [], // Override removing id attribute so streamlit app builder can use it
    attributes: [
        { name: 'caption', type: 'string' },
        { name: 'id', type: 'string' },
    ] as AttributeDef[],
    hasOptions: true,
};

const StreamlitAppNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor }) => {
    const { id, caption, streamlitXMLToLoad, options = {} } = node.attrs;
    const streamlitOptions = options as StreamlitAppOptions;
    const builderUrl = `${import.meta.env.VITE_ZYBOOKS_URL}/modern-authoring-tools/streamlit-app-builder`;

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [previewKey, setPreviewKey] = useState(0);
    const [skipDnsPoll, setSkipDnsPoll] = useState(false);
    const [connection, setConnection] = useState<StreamlitChildConnection | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const connectionRef = useRef<StreamlitChildConnection | null>(null);
    const isSavingRef = useRef(false);
    const showExitConfirmRef = useRef(false);

    const { shouldMount, isIframeLoaded, handleIframeLoad, resetLoadState } = useLazyMountIframe({
        containerRef,
        editor,
        forceMount: isEditing,
        id,
    });

    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'streamlitApp', isEditing);

    useEffect(() => {
        isSavingRef.current = isSaving;
    }, [isSaving]);

    useEffect(() => {
        showExitConfirmRef.current = showExitConfirm;
    }, [showExitConfirm]);

    const destroyConnection = useCallback(async () => {
        const conn = connectionRef.current;
        if (!conn) {
            return;
        }
        connectionRef.current = null;
        setConnection(null);
        await conn.destroy();
    }, []);

    const handleUpdateFromSave = useCallback(
        async (xml: string) => {
            const updates = parseZyXML(xml, parseStreamlitXMLConfig);
            if (updates) {
                updateAttributes(updates);
            }
            const streamlitExtension = editor.extensionManager.extensions.find(extension => extension.name === 'streamlitApp');
            const { onSyncSection } = (streamlitExtension?.options ?? {}) as StreamlitAppNodeOptions;
            if (onSyncSection) {
                try {
                    await onSyncSection(editor);
                } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Failed to sync section');
                    setIsSaving(false);
                    return;
                }
            }
            setIsSaving(false);
            setShowExitConfirm(false);
            setSkipDnsPoll(false);
            setPreviewKey(key => key + 1);
            await destroyConnection();
            resetLoadState();
            setIsEditing(false);
        },
        [editor, updateAttributes, destroyConnection, resetLoadState],
    );

    const requestExitConfirm = useCallback(() => {
        if (isSavingRef.current || showExitConfirmRef.current) {
            return;
        }
        setShowExitConfirm(true);
    }, []);

    const confirmExitWithoutSaving = useCallback(async () => {
        if (isSavingRef.current) {
            return;
        }
        setShowExitConfirm(false);
        setSkipDnsPoll(true);
        await destroyConnection();
        resetLoadState();
        setIsEditing(false);
    }, [destroyConnection, resetLoadState]);

    const handleSaveAndExit = useCallback(async () => {
        if (!connection?.saveForModernAuthoring || isSavingRef.current) {
            return;
        }

        setIsSaving(true);
        try {
            await connection.saveForModernAuthoring();
        } catch (error) {
            console.error('Failed to save Streamlit app for Modern Authoring:', error);
            setIsSaving(false);
        }
    }, [connection]);

    const handleEnterEdit = useCallback(() => {
        setIsEditing(true);
    }, []);

    useEffect(() => {
        if (!isEditing || !shouldMount || !iframeRef.current) {
            return;
        }

        let cancelled = false;

        const establishConnection = async () => {
            try {
                const connectionObj = connectToChild({
                    iframe: iframeRef.current!,
                    methods: {
                        updateParentXML: (xml: string) => {
                            handleUpdateFromSave(xml);
                        },
                        getParentXML: () => ensureStreamlitXmlId(streamlitXMLToLoad, id),
                        getBaseImageUrl: () => editor.state.doc.attrs.baseImageUrl,
                        notifyEscapeKeyPressed: () => {
                            requestExitConfirm();
                        },
                    },
                });

                const childConnection = await connectionObj.promise;
                if (cancelled) {
                    await connectionObj.destroy();
                    return;
                }

                const nextConnection = {
                    ...childConnection,
                    destroy: connectionObj.destroy,
                } as StreamlitChildConnection;
                connectionRef.current = nextConnection;
                setConnection(nextConnection);
            } catch (error) {
                console.error('Failed to establish Streamlit builder connection:', error);
                connectionRef.current = null;
                setConnection(null);
            }
        };

        void establishConnection();

        return () => {
            cancelled = true;
            void destroyConnection();
        };
        // Only reconnect when entering edit / iframe mount changes — not when connection callbacks update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, shouldMount]);

    useEffect(() => {
        if (!isEditing) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return;
            }
            requestExitConfirm();
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isEditing, requestExitConfirm]);

    const previewGuid = streamlitOptions.guid ?? id;
    const previewStreamlitAppId = streamlitOptions.streamlit_app_id;
    const editActionsDisabled = isSaving || !connection;
    const userFullName = authService.getUserFullName()?.trim();

    return (
        <CommonNodeViewWrapper
            label={isEditing ? null : 'Streamlit app'}
            selected={selected}
            containerClassName={isEditing ? 'inset-0 z-51 rounded-none' : ''}
            contentClassName="mx-0"
            position={isEditing ? 'fixed' : 'relative'}
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
        >
            <div>
                <div className="bg-gray-100 px-4 py-2 dark:bg-gray-800 border-b flex items-center justify-between h-[50px] gap-x-6">
                    <div className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                        {isEditing ? (userFullName ? `${userFullName}'s streamlit server` : 'Streamlit server') : caption}
                    </div>
                    <div className="flex items-center ml-auto gap-x-2">
                        {isEditing ? (
                            <>
                                <Tooltip
                                    delay={0}
                                    closeDelay={0}
                                >
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
                                            aria-label="Save and exit details"
                                        >
                                            <Info className="h-4 w-4" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Saving and exiting will create a non-personal streamlit server intended for student use. <br />
                                        Use the &apos;Run app&apos; button to quickly preview your changes as you work.
                                    </TooltipContent>
                                </Tooltip>
                                <Button
                                    type="button"
                                    variant="default"
                                    size="sm"
                                    disabled={editActionsDisabled}
                                    isLoading={isSaving}
                                    onClick={() => void handleSaveAndExit()}
                                >
                                    Save and exit
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={editActionsDisabled}
                                    onClick={requestExitConfirm}
                                >
                                    Exit without saving
                                </Button>
                            </>
                        ) : (
                            <ZyEditToolButton
                                isEditing={false}
                                onSetIsEditing={nextIsEditing => {
                                    if (nextIsEditing) {
                                        handleEnterEdit();
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className={isEditing ? 'h-[calc(100vh-50px)]' : ''}>
                    {isEditing ? (
                        <LazyMountIframe
                            containerRef={containerRef}
                            shouldMount={shouldMount}
                            id={id}
                            url={builderUrl}
                            title="Streamlit app builder"
                            iframeRef={iframeRef}
                            loadingMessage="Loading Streamlit builder…"
                            isIframeLoaded={isIframeLoaded}
                            onLoad={handleIframeLoad}
                            iframeClassName="h-full w-full border-0 bg-zybooks-bg"
                        />
                    ) : (
                        <StreamlitPreviewIframe
                            key={previewKey}
                            guid={previewGuid}
                            streamlitAppId={previewStreamlitAppId}
                            skipDnsPoll={skipDnsPoll}
                        />
                    )}
                </div>
            </div>

            <AlertDialog
                open={showExitConfirm}
                onOpenChange={open => {
                    if (!isSaving) {
                        setShowExitConfirm(open);
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Exit without saving?</AlertDialogTitle>
                        <AlertDialogDescription>All unsaved changes will be lost</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={isSaving}
                            onClick={() => void confirmExitWithoutSaving()}
                        >
                            Exit without saving
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </CommonNodeViewWrapper>
    );
};

export default memo(StreamlitAppNodeView);
